import type {
  OfferItem,
  ProfileData,
  ShopItem,
  ShopItems,
  VipStatusItem,
} from "./types";

const baseUrl =
  import.meta.env.VITE_API_BASE_URL || "https://server.production.durak.ru";

export type ApiResponse<TData> =
  | { success: true; data: TData }
  | { success: false; reason: string };

async function apiGet<T>(
  url: string,
  params?: Record<string, string>,
): Promise<T> {
  const query = params ? `?${new URLSearchParams(params).toString()}` : "";
  const res = await fetch(`${baseUrl}${url}${query}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as Record<string, string>).message || res.statusText);
  }
  return res.json() as Promise<T>;
}

function parseShopItem(data: Record<string, unknown>): ShopItem {
  return {
    price: data.price as number,
    id: data.id as string,
    shopId: data.shopId as string,
    isOfferItem: data.isOfferItem as boolean,
    title: data.title as string,
    group: data.group as string,
    image: data.image as string,
    discount: (data.discount as number) ?? 0,
    resources: ((data.resources as Array<Record<string, unknown>>) || []).map(
      (r) => ({
        currency: r.currency as string,
        amount: r.amount as number,
      }),
    ),
    vipResources: (
      (data.vipResources as Array<Record<string, unknown>>) || []
    ).map((r) => ({
      currency: r.currency as string,
      amount: r.amount as number,
    })),
    purchaseBonusPercent: data.purchaseBonusPercent as number | null,
    labelType: (data.labelType as string) ?? null,
    isVisible: (data.isVisible as boolean) ?? true,
  };
}

function parseOfferItem(data: Record<string, unknown>): OfferItem {
  return {
    ...parseShopItem(data),
    offerName: data.offerName as string,
    expirationDate: data.expirationDate as number,
  };
}

function parseVipItem(data: Record<string, unknown>): VipStatusItem {
  return {
    ...parseShopItem(data),
    vipDurationDays: data.vipDurationDays as number,
  };
}

export async function fetchUserProfile(
  playerId: number,
): Promise<ApiResponse<ProfileData>> {
  try {
    const data = await apiGet<Record<string, unknown>>(
      "/api/v7/portal/profile",
      {
        playerId: String(playerId),
      },
    );
    return {
      success: true,
      data: { id: playerId, ...data } as ProfileData,
    };
  } catch (error) {
    const reason = error instanceof Error ? error.message : "Unknown error";
    return { success: false, reason };
  }
}

export async function fetchShopItems(
  playerId: number,
): Promise<ApiResponse<ShopItems>> {
  try {
    const data = await apiGet<Record<string, unknown>>(
      "/api/v7/shop/smart-glocal/portal/shop-items",
      {
        playerId: String(playerId),
      },
    );

    const chips: ShopItem[] = [];
    const tickets: ShopItem[] = [];

    for (const itemData of (data.resourcesItems as Array<
      Record<string, unknown>
    >) || []) {
      const item = parseShopItem(itemData);
      if (item.group === "Chips") chips.push(item);
      else if (item.group === "Tickets") tickets.push(item);
    }

    const offers = ((data.offers as Array<Record<string, unknown>>) || []).map(
      parseOfferItem,
    );
    const vipStatusItems = (
      (data.vipItems as Array<Record<string, unknown>>) || []
    ).map(parseVipItem);

    return {
      success: true,
      data: {
        chips,
        tickets,
        offers,
        vipStatusItems,
        vipStatus: data.vipStatus as VipStatus,
      },
    };
  } catch (error) {
    const reason = error instanceof Error ? error.message : "Unknown error";
    return { success: false, reason };
  }
}

export async function getSmartGlocalToken(
  productId: string,
  playerId: number,
  returnUrl?: string,
): Promise<string | null> {
  try {
    const params = new URLSearchParams({
      playerId: String(playerId),
      itemId: productId,
      returnUrl: returnUrl || window.location.origin,
    });
    const res = await fetch(
      `${baseUrl}/api/v7/shop/smart-glocal/portal/paymentToken?${params}`,
      {
        method: "POST",
        headers: {
          "PLVHeart-Environment": "webShop",
        },
      },
    );
    if (!res.ok) return null;
    const token = await res.text();
    return token || null;
  } catch {
    return null;
  }
}

export interface SbpSessionResponse {
  sessionId: string;
  status: "pending" | "awaiting_payment" | "paid" | "failed";
  qrContent: string | null;
  qrImageUrl: string | null;
  expiresInSeconds: number;
}

export async function createSbpSession(
  productId: string,
  playerId: number,
  returnUrl?: string,
): Promise<SbpSessionResponse | null> {
  try {
    const params = new URLSearchParams({
      playerId: String(playerId),
      itemId: productId,
      returnUrl: returnUrl || window.location.origin + "/#/payment-status",
    });
    const res = await fetch(
      `${baseUrl}/api/v7/shop/smart-glocal/portal/sbp/create?${params}`,
      {
        method: "POST",
        headers: {
          "PLVHeart-Environment": "webShop",
        },
      },
    );
    if (!res.ok) return null;
    return (await res.json()) as SbpSessionResponse;
  } catch {
    return null;
  }
}

export async function getSbpSessionStatus(
  sessionId: string,
): Promise<SbpSessionResponse | null> {
  try {
    const params = new URLSearchParams({ sessionId });
    const res = await fetch(
      `${baseUrl}/api/v7/shop/smart-glocal/portal/sbp/status?${params}`,
    );
    if (!res.ok) return null;
    return (await res.json()) as SbpSessionResponse;
  } catch {
    return null;
  }
}
